import React from "react";

// -------- Module-level memoised SDK loader --------
const sdkPromises = {};
function loadSquareSdk(env) {
    const src = env === "sandbox"
        ? "https://sandbox.web.squarecdn.com/v1/square.js"
        : "https://web.squarecdn.com/v1/square.js";
    if (sdkPromises[src]) return sdkPromises[src];
    sdkPromises[src] = new Promise((resolve, reject) => {
        if (window.Square) return resolve(window.Square);
        // Reuse a healthy existing script only if it looks live; a prior errored
        // script node still sits in the DOM after failure and will never re-fire
        // its load/error events, so replace it on retry.
        const existing = document.querySelector(`script[src="${src}"]`);
        if (existing && !existing.dataset.sqFailed) {
            existing.addEventListener("load", () => resolve(window.Square));
            existing.addEventListener("error", () => reject(new Error("Square SDK failed to load")));
            if (window.Square) resolve(window.Square);
        } else {
            if (existing) existing.remove();
            const s = document.createElement("script");
            s.src = src; s.async = true;
            s.onload = () => resolve(window.Square);
            s.onerror = () => { s.dataset.sqFailed = "1"; reject(new Error("Square SDK failed to load")); };
            document.body.appendChild(s);
        }
        // Settle-timeout: never hang the UI on 'Loading secure card form…'.
        setTimeout(() => reject(new Error("Square SDK load timed out")), 12000);
    }).catch((e) => {
        // Don't memoise a rejection — a retry must be able to re-request the SDK.
        delete sdkPromises[src];
        document.querySelectorAll(`script[src="${src}"]`).forEach((n) => n.remove());
        throw e;
    });
    return sdkPromises[src];
}

// -------- Per-container serialised attach/destroy chain --------
// chains[id] is a Promise<card|null>. Every mount chains onto it with an
// attach step; every unmount chains onto it with a destroy step. This
// guarantees no two attach() or destroy() calls ever race on the same DOM
// container, which was the root cause of the "0 iframes, Pay disabled forever"
// bug on repeated SPA navigations to /checkout/:id.
const chains = {};

function enqueueAttach(containerId, config) {
    // `.catch(() => null)` prevents a prior failure from poisoning the chain
    // for this container — every enqueued step starts from a fresh resolved
    // state, so Retry (or a fresh mount) can actually try again.
    const prev = (chains[containerId] || Promise.resolve(null)).catch(() => null);
    const next = prev.then(async (existing) => {
        // If a prior mount left a live card, destroy it before creating a new
        // one — every effect run owns its own card object.
        if (existing) { try { await existing.destroy(); } catch (_) { /* noop */ } }
        const Sq = await loadSquareSdk(config.square_env);
        const container = document.getElementById(containerId);
        if (!container) return null;
        const payments = Sq.payments(config.square_app_id, config.square_location_id);
        const card = await payments.card();
        await card.attach(`#${containerId}`);
        return card;
    });
    chains[containerId] = next;
    return next;
}

function enqueueDestroy(containerId) {
    const prev = (chains[containerId] || Promise.resolve(null)).catch(() => null);
    const next = prev.then(async (card) => {
        if (card) { try { await card.destroy(); } catch (_) { /* noop */ } }
        return null;
    });
    chains[containerId] = next;
    return next;
}

// -------- Hook --------
// Returns { cardRef, cardReady, cardError, retry }.
export function useSquareCard(containerId, config, when = true) {
    const cardRef = React.useRef(null);
    const [cardReady, setCardReady] = React.useState(false);
    const [cardError, setCardError] = React.useState(null);
    const [attempt, setAttempt] = React.useState(0);

    React.useEffect(() => {
        if (!when || !config || config.square_mock_mode) return undefined;
        let cancelled = false;
        setCardError(null);

        const attachPromise = enqueueAttach(containerId, config);
        attachPromise
            .then((card) => {
                if (cancelled) return;
                if (!card) {
                    setCardError("Payment form failed to load");
                    return;
                }
                cardRef.current = card;
                setCardReady(true);
            })
            .catch((err) => {
                if (cancelled) return;
                // Log so it's diagnosable — do NOT swallow silently.
                console.error("[useSquareCard] attach failed", err);
                setCardError("Payment form failed to load");
            });

        return () => {
            cancelled = true;
            cardRef.current = null;
            setCardReady(false);
            enqueueDestroy(containerId);
        };
    }, [containerId, config, when, attempt]);

    const retry = React.useCallback(() => {
        setCardError(null);
        setCardReady(false);
        setAttempt((a) => a + 1);
    }, []);

    return { cardRef, cardReady, cardError, retry };
}
